import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, ShieldCheck } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Segmentado, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { post } from '../../nucleo/api';
import { dataHora, nomePessoa, numero, relativo } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { BASE_BROADCAST, chaveBroadcasts, COR_ESTADO_BROADCAST, telefoneTela, type BroadcastResumo, type Consentimento } from './tipos-broadcast';

type Filtro = 'todos' | 'rascunho' | 'agendado' | 'enviando' | 'pausado' | 'concluido' | 'cancelado';
const FILTROS: Filtro[] = ['todos', 'rascunho', 'agendado', 'enviando', 'pausado', 'concluido', 'cancelado'];

/**
 * U14 F5: Automações > Broadcast. A lista dos disparos de template do WhatsApp (com enviados, entregues, lidos, falhas e
 * cliques), "Novo disparo" e o registro dos consentimentos.
 */
export function Broadcasts() {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [novo, setNovo] = useState(false);
  const [consentimentos, setConsentimentos] = useState(false);
  const podeEditar = s.pode('automacoes.editar');
  const colunas: Coluna<BroadcastResumo>[] = [
    { chave: 'nome', rotulo: t('broadcast.colunas.nome'), principal: true },
    { chave: 'estado', rotulo: t('broadcast.colunas.estado'), render: (b) => <Selo cor={COR_ESTADO_BROADCAST[b.estado]}>{t(`broadcast.estados.${b.estado}`)}</Selo> },
    { chave: 'template_nome', rotulo: t('broadcast.colunas.template'), render: (b) => b.template_nome ?? '' },
    { chave: 'agendado_para', rotulo: t('broadcast.colunas.quando'), render: (b) => (b.iniciado_em ? dataHora(b.iniciado_em) : b.agendado_para ? dataHora(b.agendado_para) : relativo(b.updated_at)) },
    { chave: 'enviados', rotulo: t('broadcast.colunas.enviados'), alinhar: 'direita', render: (b) => numero(b.contagem.enviados) },
    { chave: 'entregues', rotulo: t('broadcast.colunas.entregues'), alinhar: 'direita', render: (b) => numero(b.contagem.entregues) },
    { chave: 'lidos', rotulo: t('broadcast.colunas.lidos'), alinhar: 'direita', render: (b) => numero(b.contagem.lidos) },
    { chave: 'falhas', rotulo: t('broadcast.colunas.falhas'), alinhar: 'direita', render: (b) => numero(b.contagem.falhas) },
    { chave: 'cliques', rotulo: t('broadcast.colunas.cliques'), alinhar: 'direita', render: (b) => numero(b.contagem.cliques) },
  ];
  const campos: DefCampo[] = [{ nome: 'nome', rotulo: t('broadcast.nome'), tipo: 'texto', obrigatorio: true, max: 120 }];
  return (
    <div className="flex min-w-0 flex-col gap-4" data-broadcasts>
      <AcaoPrimaria>
        <Botao icone={<ShieldCheck className="size-4" />} onClick={() => setConsentimentos(true)}>
          {t('broadcast.consentimentos.abrir')}
        </Botao>
        {podeEditar && (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('broadcast.novo')}
          </Botao>
        )}
      </AcaoPrimaria>
      <p className="max-w-prose text-sm text-tinta-2">{t('broadcast.grupo')}</p>
      <Tabela<BroadcastResumo>
        tela="automacoes.broadcasts"
        caminho="/automacoes/broadcasts"
        params={filtro === 'todos' ? {} : { estado: filtro }}
        chaveConsulta={[...chaveBroadcasts, 'lista', filtro]}
        colunas={colunas}
        filtros={{ filtro }}
        aoAplicarFiltros={(x) => setFiltro(FILTROS.includes(x.filtro as Filtro) ? (x.filtro as Filtro) : 'todos')}
        filtrosExtra={<Segmentado<Filtro> rotulo={t('broadcast.colunas.estado')} valor={filtro} opcoes={FILTROS.map((f) => ({ valor: f, rotulo: t(`broadcast.filtros.${f}`) }))} aoMudar={setFiltro} />}
        aoClicar={(b) => navegar(`${BASE_BROADCAST}/disparo/${b.id}`)}
        vazio={<Vazio texto={podeEditar ? t('broadcast.vazio') : t('broadcast.vazioLeitura')} />}
        buscaNaUrl
      />
      {novo && (
        <Drawer
          aberto
          aoFechar={() => setNovo(false)}
          titulo={t('broadcast.novo')}
          rodape={
            <>
              <Botao variante="fantasma" onClick={() => setNovo(false)}>
                {tn('comum.cancelar')}
              </Botao>
              <Botao variante="primario" type="submit" form="form-broadcast-novo">
                {tn('comum.salvar')}
              </Botao>
            </>
          }
        >
          <Formulario
            id="form-broadcast-novo"
            campos={campos}
            aoEnviar={async (v) => {
              const id = crypto.randomUUID();
              await post('/automacoes/broadcasts', { id, nome: v.nome });
              await qc.invalidateQueries({ queryKey: chaveBroadcasts });
              toast.sucesso(t('broadcast.criado'));
              setNovo(false);
              navegar(`${BASE_BROADCAST}/disparo/${id}`);
            }}
          />
        </Drawer>
      )}
      {consentimentos && <Consentimentos aoFechar={() => setConsentimentos(false)} />}
    </div>
  );
}

/** O registro dos consentimentos do WhatsApp (autorizou ou saiu, com a origem e quem registrou) e o registro manual. */
function Consentimentos({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('automacoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [registrar, setRegistrar] = useState(false);
  const colunas: Coluna<Consentimento>[] = [
    { chave: 'contato', rotulo: t('broadcast.consentimentos.colunas.contato'), principal: true, render: (c) => (c.parceiro_nome ? `${nomePessoa(c.parceiro_nome)} · ${telefoneTela(c.contato)}` : telefoneTela(c.contato)) },
    { chave: 'tipo', rotulo: t('broadcast.consentimentos.colunas.tipo'), render: (c) => t(`broadcast.tipos.${c.tipo}`) },
    { chave: 'situacao', rotulo: t('broadcast.consentimentos.colunas.situacao'), render: (c) => <Selo cor={c.situacao === 'saiu' ? 'perigo' : 'verde'}>{t(`broadcast.consentimentos.situacoes.${c.situacao}`)}</Selo> },
    { chave: 'origem', rotulo: t('broadcast.consentimentos.colunas.origem'), render: (c) => t(`broadcast.consentimentos.origens.${c.origem}`, { defaultValue: c.origem }) },
    { chave: 'registrado_por_nome', rotulo: t('broadcast.consentimentos.colunas.quem'), render: (c) => (c.registrado_por_nome ? nomePessoa(c.registrado_por_nome) : t('broadcast.consentimentos.sistema')) },
    { chave: 'em', rotulo: t('broadcast.consentimentos.colunas.quando'), render: (c) => dataHora(c.em) },
  ];
  const campos: DefCampo[] = [
    { nome: 'telefone', rotulo: t('broadcast.consentimentos.telefone'), tipo: 'telefone', obrigatorio: true },
    {
      nome: 'tipos',
      rotulo: t('broadcast.consentimentos.tipos'),
      tipo: 'selecao',
      obrigatorio: true,
      opcoes: (['ambos', 'marketing', 'avisos'] as const).map((v) => ({ valor: v, rotulo: t(`broadcast.consentimentos.tiposOpcoes.${v}`) })),
    },
    { nome: 'situacao', rotulo: t('broadcast.consentimentos.situacao'), tipo: 'selecao', obrigatorio: true, opcoes: (['autorizou', 'saiu'] as const).map((v) => ({ valor: v, rotulo: t(`broadcast.consentimentos.situacoes.${v}`) })) },
    { nome: 'observacao', rotulo: t('broadcast.consentimentos.observacao'), tipo: 'area', obrigatorio: true, max: 500 },
  ];
  return (
    <Drawer
      aberto
      largura="lg"
      aoFechar={aoFechar}
      titulo={t('broadcast.consentimentos.titulo')}
      rodape={
        registrar ? (
          <>
            <Botao variante="fantasma" onClick={() => setRegistrar(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-consentimento">
              {tn('comum.salvar')}
            </Botao>
          </>
        ) : s.pode('automacoes.editar') && s.pode('conversas.editar') ? (
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setRegistrar(true)}>
            {t('broadcast.consentimentos.registrar')}
          </Botao>
        ) : undefined
      }
    >
      {registrar ? (
        <Formulario
          id="form-consentimento"
          campos={campos}
          inicial={{ tipos: 'marketing', situacao: 'saiu' }}
          aoEnviar={async (v) => {
            const tipos = v.tipos === 'ambos' ? ['marketing', 'avisos'] : [v.tipos];
            await post('/automacoes/consentimentos', { telefone: v.telefone, tipos, situacao: v.situacao, observacao: v.observacao });
            await qc.invalidateQueries({ queryKey: ['automacoes', 'consentimentos'] });
            toast.sucesso(t('broadcast.consentimentos.registrado'));
            setRegistrar(false);
          }}
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <p className="text-sm text-tinta-2">{t('broadcast.consentimentos.texto')}</p>
          <Tabela<Consentimento>
            tela="automacoes.consentimentos"
            caminho="/automacoes/consentimentos"
            chaveConsulta={['automacoes', 'consentimentos']}
            colunas={colunas}
            vazio={<Vazio texto={t('broadcast.consentimentos.vazio')} />}
          />
        </div>
      )}
    </Drawer>
  );
}
